// helpers/getPageTitle.ts

/**
 * Returns the back navigation route for pages that have a back button
 * Returns null if the page doesn't need a back button
 */
export function getBackRoute(path: string, search = ""): string | null {
  // Routes that have back buttons in their tabs
  if (path === "/kontrol/port-details") {
    return "/kontrol/port";
  }
  if (path === "/kontrol/cloud-connectivity/details") {
    return "/kontrol/cloud-connectivity";
  }
  if (path === "/kontrol/datacenter-details") {
    return "/kontrol/datacenter";
  }
  if (path === "/kontrol/internet/internet-details") {
    return "/kontrol/ipx";
  }
  if (path === "/kontrol/backbone-details") {
    return "/kontrol/backbone";
  }
  if (path === "/kontrol/sdwan-hub-details") {
    return "/kontrol/sdwan-hub";
  }
  if (path === "/kontrol/extender-pop-details") {
    return "/kontrol/extender-pop";
  }
  if (path === "/kontrol/extender-pop-cpe-details") {
    // A CPE detail is reached from its site's detail: go back to that site,
    // not to the list, the same way a branch goes back to its hub.
    const extenderPopId = new URLSearchParams(search).get("extenderPopId");
    return extenderPopId
      ? `/kontrol/extender-pop-details?extenderPopId=${encodeURIComponent(extenderPopId)}`
      : "/kontrol/extender-pop";
  }
  if (path === "/kontrol/carrier-access-details") {
    return "/kontrol/carrier-access";
  }
  if (path === "/kontrol/sdwan-branch-details") {
    // Branch detail is a 3rd-level view reached from a specific hub detail
    // (hub list → hub detail → branch detail). Go back to that hub detail
    // using the sdwanId carried in the query string — not to the hub list,
    // which would drop the user two levels up.
    const sdwanId = new URLSearchParams(search).get("sdwanId");
    return sdwanId
      ? `/kontrol/sdwan-hub-details?sdwanId=${encodeURIComponent(sdwanId)}`
      : "/kontrol/sdwan-hub";
  }
  if (path === "/kontrol/outages") {
    return "/kontrol";
  }
  // Handle dynamic routes for reflector and cluster details
  if (path.includes("/reflector-v2/management/bgp-reflectors/details")) {
    return "/reflector-v2/management/bgp-reflectors";
  }
  if (path.includes("/reflector-v2/management/clusters/details")) {
    return "/reflector-v2/management/clusters";
  }
  if (path.match(/^\/support\/audit-logs\/[^/]+$/)) {
    return "/support/audit-logs";
  }
  // Handle presales designer detail view (with order ID)
  if (path.match(/\/kontrol\/admin-center\/presales-designer\/\d+/)) {
    return "/kontrol/admin-center/presales-designer";
  }
  // Tags Manager: create + edit views go back to the list
  if (
    path === "/account/tags-manager/new" ||
    path.match(/^\/account\/tags-manager\/[^/]+$/)
  ) {
    return "/account/tags-manager";
  }
  if (
    path === "/account/notifications/groups/new" ||
    path.match(/^\/account\/notifications\/groups\/[^/]+$/)
  ) {
    return "/account/notifications";
  }

  return null;
}

/**
 * The top-bar heading for a route. `null` means the route renders no heading at all — the
 * page fills the title row itself (see `SideNavTitleSlot`) and needs that width more than a
 * label the rail already shows.
 */
export function getPageTitle(path: string): string | null {
  // Handle dynamic routes for reflector and cluster details
  if (path.includes("/reflector-v2/management/bgp-reflectors/details")) {
    return "Reflector Details";
  }
  
  if (path.includes("/reflector-v2/management/clusters/details")) {
    return "Cluster Details";
  }
  
  // Handle presales designer detail view (with order ID)
  if (path.match(/\/kontrol\/admin-center\/presales-designer\/\d+/)) {
    return "Service Designer";
  }

  // Audit log detail (`/support/audit-logs/:id`) — match anything below
  // the list path that isn't the list itself.
  if (path.match(/^\/support\/audit-logs\/[^/]+$/)) {
    return "Audit Log Details";
  }

  // Tags Manager dynamic routes
  if (path === "/account/tags-manager/new") {
    return "Create Tag";
  }
  if (path.match(/^\/account\/tags-manager\/[^/]+$/)) {
    return "Edit Tag";
  }

  if (
    path === "/account/notifications/groups/new" ||
    (path.match(/^\/account\/notifications\/groups\/[^/]+$/) &&
      path !== "/account/notifications/groups/new")
  ) {
    return "Notifications";
  }

  if (path === "/changelog") {
    return "Release Notes";
  }

  switch (path) {
    case "/":
      return "Home";
    case "/reflector/management/bgp/neighbors":
      return "BGP";
    case "/reflector/management/bgp/status":
      return "BGP";
    case "/reflector/reporting":
      return "Reporting";
    case "/kontrol/admin-center":
    case "/kontrol/admin-center/datacenter-inventory":
      return "Datacenter Inventory";
    case "/kontrol/admin-center/hardware":
      return "Hardware Inventory";
    case "/kontrol/admin-center/hardware/port":
      return "Port Inventory";
    case "/kontrol/admin-center/datacenter-connections":
      return "Dataceter Connections";
    case "/kontrol/admin-center/third-party":
    case "/kontrol/admin-center/port":
    case "/kontrol/admin-center/lastMile":
    case "/kontrol/admin-center/dc-connections":
    case "/kontrol/admin-center/ip-block":
    case "/kontrol/admin-center/public-subnet":
    case "/kontrol/admin-center/partners":
    case "/kontrol/admin-center/customers":
    case "/kontrol/admin-center/internet":
      return "Resource Manager";
    case "/kontrol/admin-center/ke-topology":
      return "Network Insight";
    case "/reflector/management/licensing":
      return "Licensing Management";
    case "/reflector/admin-center/management/licensing":
      return "License Creation";
    case "/kontrol":
      return "Overview";
    case "/kontrol/outages":
        return " ";
    case "/kontrol/port":
      return "Port";
    case "/kontrol/add-port":
      return "Create Port";
    case "/kontrol/ipx":
      return "Internet Connection";
    case "/kontrol/add-ipx":
      return "Create Connection";
    case "/kontrol/datacenter":
      return "Data Center Interconnect";
    case "/kontrol/add-dc":
      return "Create DataCenter";
    case "/kontrol/cloud-connectivity":
      return "Cloud Connectivity";
    case "/kontrol/cloud-connectivity/details":
      return "Cloud Connectivity Details";
    case "/kontrol/add-cc":
      return "Create Cloud Connectivity";
    case "/kontrol/backbone":
      return "Backbone";
    case "/kontrol/backbone-details":
      return "Backbone Details";
    case "/kontrol/add-backbone":
      return "Create Backbone";
    case "/kontrol/port-details":
      return "Port Details";
    case "/kontrol/internet/internet-details":
      return "Internet Connection Details";
    case "/kontrol/datacenter-details":
      return "Data Center Interconnect Details";
    case "/kontrol/fwaas":
      return "FWaaS";
    case "/kontrol/sdwan-hub":
      return "SD-WAN Hub";
    case "/kontrol/extender-pop":
      return "Extended PoP";
    case "/kontrol/extender-pop-details":
      return "Extended PoP Details";
    case "/kontrol/extender-pop-cpe-details":
      return "CPE Details";
    case "/kontrol/carrier-access":
      return "Carrier Access";
    case "/kontrol/carrier-access-details":
      return "Carrier Access Details";
    case "/kontrol/sdwan-hub-details":
      return "SD-WAN Hub";
    case "/kontrol/sdwan-branch-details":
      return "Branch";
    case "/kontrol/reports":
      return "Reports";
    case "/kontrol/quotations":
      return "Quotations";
    case "/kontrol/alerts":
      return "Alerts";
    case "/kontrol/internet-ip-monitoring":
      return "Internet monitoring";
    case "/kontrol/admin-center/presales-designer":
      return "Presales Designer";
    case "/kontrol/admin-center/ai-quotation":
      return "AI Quotation Assistant";
    case "/reflector/l3overview/bgp":
      return "L3 Overview";
    case "/reflector/l3overview/logs":
      return "L3 Overview";
    case "/reflector/status":
      return "Status";
    case "/reflector/management/instances/licensed":
    case "/reflector/management/instances/unlicensed":
    case "/reflector/management/instances/highavailability":
    case "/reflector/management/instances/healthcheck":
    case "/reflector/management/instances/syslog":
      return "Instances management";
    case "/account/profile":
      return "Profile";
    case "/account/users":
      return "Users";
    case "/account/customers":
      return "Customers";
    case "/kontrol/notifications-management":
      return "Notifications management";
    case "/account/notifications":
      return "Notifications";
    case "/account/tags-manager":
      return "Tags Manager";
    case "/account/api":
      return "API";
    case "/kontrol/notifications-history":
      return "Notifications history";
    case "/request":
      return "Request";
    case "/kontrol/dashboard":
      return "Overview";
    case "/support/request":
      return "Technical Support";
    case "/support/audit-logs":
      return "Audit Logs";
    case "/reflector-v2/overview":
      return "Overview";
    case "/reflector-v2/management/bgp-reflectors":
      return "Reflectors";
    case "/reflector-v2/management/clusters":
      return "Clusters";
    case "/reflector-v2/management/bgp-reflectors/details":
      return "BGP Reflector Details";
    case "/reflector-v2/management/clusters/details":
      return "Cluster Details";
    case "/reflector-v2/management/licensing":
      return "License Management";
    case "/reflector-v2/admin-center/management/licensing":
      return "License Admin Management";
    case "/reflector-v2/management/licensing/payg":
      return "License Management";
    case "/reflector-v2/management/bgp-visibility":
      // Its toolbar owns the whole title row; the heading pushed it onto a second line (I6).
      return null;
    case "/reflector-v2/logs":
      return "Logs";
    case "/reflector-v2/network-topology":
      return "Network Topology";
    case "/reflector-v2/reporting":
      return "Reporting";
    case "/reflector-v2/admin-center/releases":
      return "Releases";
    case "/insight":
    case "/insight/home":
      return "Home";
    case "/insight/topology":
      return "Network Topology";
    case "/insight/optimization":
      return "Optimization Hub";
    default:
      if (path.startsWith("/insight/topology/") || path.startsWith("/reflector-v2/network-topology")) {
        return "Network Topology";
      }
      if (path.startsWith("/insight/optimization/")) {
        return "Optimization Hub";
      }
      return "My View";
  }
}
